// views/Profile/components/WalletSummaryInsights.jsx
import React from 'react';
import {
  CalendarClock,
  TrendingUp,
  Activity,
  Sparkles,
  ArrowUpRight,
  ArrowDownRight,
  ArrowRight,
} from 'lucide-react';
import { daysBetween, estimateNextReset } from '../utils/billingCycle';
import './WalletSummaryInsights.css';

const nf = new Intl.NumberFormat('en-US');
const formatCoins = (v) =>
  v == null || Number.isNaN(v) ? '—' : nf.format(Math.round(v));

const RESOURCE_LABELS = {
  WORKFLOW_EXECUTION: 'Workflow runs',
  WORKFLOW_STEP: 'Workflow steps',
  ASSISTANT_REQUEST: 'Agent requests',
  ASSISTANT_CALL: 'Agent requests',
  DATABASE_CALL: 'Database calls',
  DATABASE_READ: 'Database reads',
  DATABASE_WRITE: 'Database writes',
  DATABASE_QUERY: 'Database queries',
  DATA_TRANSFER_GB: 'Data transfer',
  TABLE_OPERATION: 'Table operations',
  INTEGRATION_CALL: 'Integration calls',
  IMAGE_GENERATION: 'Image generation',
  BONUS: 'Bonus credits',
};

const humanize = (raw) => {
  if (!raw) return 'Other';
  if (RESOURCE_LABELS[raw]) return RESOURCE_LABELS[raw];
  const w = raw.split('_');
  return w
    .map((x, i) => (i === 0 ? x[0] + x.slice(1).toLowerCase() : x.toLowerCase()))
    .join(' ');
};

const RESOURCE_COLORS = ['#8b3dff', '#5b7cff', '#3fbf90', '#e0709a', '#d4a5ff'];


const relativeTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * WalletSummaryInsights — the "so what?" layer under the summary cards.
 *
 * Everything here is derived on the client from data the page already
 * has (usage byResource, tier allowance, billing cycle, the loaded
 * ledger) — no extra requests. It turns raw numbers into decisions:
 * a burn-rate runway banner, four KPI tiles, the top spenders and a
 * recent-activity peek that hand off to the Usage / Transactions tabs.
 */
export default function WalletSummaryInsights({
  totalCharged = 0,
  monthlyAllowance = null,
  byResource = [],
  periodStart = null,
  anchorDate = null,
  lastResetAt = null,
  extraBalance = null,
  transactions = [],
  onViewUsage,
  onViewTransactions,
}) {
  const now = new Date();

  // --- Burn rate & runway --------------------------------------------
  const cycleStart = periodStart
    ? new Date(periodStart)
    : lastResetAt
    ? new Date(lastResetAt)
    : null;
  const daysElapsed =
    cycleStart && !Number.isNaN(cycleStart.getTime())
      ? Math.max(1, daysBetween(cycleStart, now))
      : null;
  const burnPerDay = daysElapsed ? totalCharged / daysElapsed : null;

  const nextReset = estimateNextReset({ anchorDate, lastResetAt });
  const daysToReset = nextReset ? Math.ceil(daysBetween(now, nextReset)) : null;

  const remaining =
    monthlyAllowance != null ? Math.max(0, monthlyAllowance - totalCharged) : null;
  const runwayDays =
    burnPerDay && burnPerDay > 0 && remaining != null
      ? remaining / burnPerDay
      : null;
  const coversCycle =
    runwayDays == null || daysToReset == null
      ? null
      : runwayDays >= daysToReset;

  // --- Events & top resources ----------------------------------------
  const totalEvents = byResource.reduce((s, r) => s + (r.count ?? 0), 0);
  const topResources = [...byResource]
    .filter((r) => (r.totalCoins ?? 0) > 0)
    .sort((a, b) => (b.totalCoins ?? 0) - (a.totalCoins ?? 0))
    .slice(0, 3);
  const maxCoins = topResources[0]?.totalCoins ?? 0;

  const recent = transactions.filter((t) => t.amount !== 0).slice(0, 4);

  const hasRunway = burnPerDay != null && remaining != null;

  return (
    <div className="wsi">
      {/* Runway insight banner */}
      {hasRunway && (
        <div
          className={`wsi-banner${
            coversCycle === false ? ' wsi-banner--warn' : ''
          }`}
        >
          <span className="wsi-banner__icon">
            <Sparkles size={18} aria-hidden="true" />
          </span>
          <p className="wsi-banner__text">
            At <strong>~{formatCoins(burnPerDay)} coins/day</strong>, your
            remaining allowance lasts{' '}
            <strong>
              {runwayDays == null || !Number.isFinite(runwayDays)
                ? 'the whole cycle'
                : `~${Math.round(runwayDays)} days`}
            </strong>
            {coversCycle === false && daysToReset != null ? (
              <>
                {' '}— that runs out{' '}
                <strong>
                  ~{Math.max(0, daysToReset - Math.round(runwayDays))} days
                </strong>{' '}
                before your reset.
              </>
            ) : daysToReset != null ? (
              <>
                {' '}— comfortably covers this cycle (resets in {daysToReset}{' '}
                {daysToReset === 1 ? 'day' : 'days'}).
              </>
            ) : (
              '.'
            )}
          </p>
        </div>
      )}

      {/* KPI tiles */}
      <div className="wsi-tiles">
        <div className="wsi-tile">
          <span className="wsi-tile__icon"><CalendarClock size={16} /></span>
          <span className="wsi-tile__value">
            {daysToReset != null ? daysToReset : '—'}
            {daysToReset != null && <small>d</small>}
          </span>
          <span className="wsi-tile__label">Until reset</span>
        </div>
        <div className="wsi-tile">
          <span className="wsi-tile__icon"><TrendingUp size={16} /></span>
          <span className="wsi-tile__value">
            {burnPerDay != null ? formatCoins(burnPerDay) : '—'}
          </span>
          <span className="wsi-tile__label">Avg coins / day</span>
        </div>
        <div className="wsi-tile">
          <span className="wsi-tile__icon"><Activity size={16} /></span>
          <span className="wsi-tile__value">{nf.format(totalEvents)}</span>
          <span className="wsi-tile__label">Events this period</span>
        </div>
        {/* Sin "Coins remaining": es el mismo número que la tarjeta de saldo
            de arriba muestra en grande. Una métrica importante tiene una
            representación principal, no tres. */}
      </div>

      {/* Top spenders + recent activity */}
      <div className="wsi-panels">
        <section className="wsi-panel">
          <header className="wsi-panel__head">
            <h3>Top spenders</h3>
            {onViewUsage && (
              <button type="button" className="wsi-link" onClick={onViewUsage}>
                Full breakdown <ArrowRight size={13} aria-hidden="true" />
              </button>
            )}
          </header>
          {topResources.length > 0 ? (
            <ul className="wsi-top">
              {topResources.map((r, i) => {
                const share =
                  totalCharged > 0
                    ? Math.round((r.totalCoins / totalCharged) * 100)
                    : 0;
                const w = maxCoins > 0 ? (r.totalCoins / maxCoins) * 100 : 0;
                const color = RESOURCE_COLORS[i % RESOURCE_COLORS.length];
                return (
                  <li key={r.resourceType} className="wsi-top__row">
                    <div className="wsi-top__meta">
                      <span className="wsi-top__name">
                        <span
                          className="wsi-top__dot"
                          style={{ background: color }}
                          aria-hidden="true"
                        />
                        {humanize(r.resourceType)}
                      </span>
                      {/* Con la unidad: "38 46%" no decía si eran monedas,
                          ejecuciones o costo. */}
                      <span className="wsi-top__num">
                        {formatCoins(r.totalCoins)} <small>coins · {share}%</small>
                      </span>
                    </div>
                    <div className="wsi-top__bar">
                      <span style={{ width: `${w}%`, background: color }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="wsi-empty">Nothing has been charged this period yet.</p>
          )}
        </section>

        <section className="wsi-panel">
          <header className="wsi-panel__head">
            <h3>Recent activity</h3>
            {onViewTransactions && (
              <button
                type="button"
                className="wsi-link"
                onClick={onViewTransactions}
              >
                All movements <ArrowRight size={13} aria-hidden="true" />
              </button>
            )}
          </header>
          {recent.length > 0 ? (
            <ul className="wsi-recent">
              {recent.map((t) => {
                const neg = t.amount < 0;
                return (
                  <li key={t.id} className="wsi-recent__row">
                    <span
                      className={`wsi-recent__ic${
                        neg ? '' : ' wsi-recent__ic--pos'
                      }`}
                    >
                      {neg ? (
                        <ArrowDownRight size={14} aria-hidden="true" />
                      ) : (
                        <ArrowUpRight size={14} aria-hidden="true" />
                      )}
                    </span>
                    <span className="wsi-recent__label">
                      {humanize(t.resourceType)}
                      <small>{relativeTime(t.createdAt)}</small>
                    </span>
                    <span
                      className={`wsi-recent__amt${
                        neg ? '' : ' wsi-recent__amt--pos'
                      }`}
                    >
                      {neg ? '' : '+'}
                      {nf.format(t.amount)}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="wsi-empty">Coin movements will show up here as they happen.</p>
          )}
        </section>
      </div>
    </div>
  );
}
